/**
 * ForceLogoutDialog — non-dismissable modal shown when the Identity
 * hub `UpdateUser` event fires for the current user (another active
 * session was started for the same user + app type).
 *
 * Flutter ref: `session_helper.dart:225-238` (`showForceLogoutPopup`)
 * with `hideNo: true` (single OK button, no Cancel) and verbatim copy
 * from `intl_en.arb:352-353`.
 *
 * Non-dismissable enforcement (Samer review PR #286, 2026-05-21):
 *  1. `showCloseButton={false}` removes the X button.
 *  2. Root `<Dialog>` receives a no-op `onOpenChange` — base-ui's
 *     internal Esc / overlay-click handlers dispatch
 *     `onOpenChange(false)`, which is intercepted here and discarded.
 *     This is the declarative path on the Root primitive (cleaner than
 *     a DOM onKeyDown listener and works regardless of focus state).
 *  3. Only the OK button's `onConfirm` callback advances the flow —
 *     that handler clears the session via auth-store.logout(), which
 *     flips `status` to `'unauthenticated'` and unmounts the dialog
 *     naturally as the parent re-renders.
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export interface ForceLogoutDialogProps {
  open: boolean;
  onConfirm: () => void;
}

export function ForceLogoutDialog({ open, onConfirm }: ForceLogoutDialogProps) {
  return (
    <Dialog
      open={open}
      // No-op onOpenChange swallows all base-ui-internal dismissal
      // requests (Escape, overlay click, focus-loop escape). Only the
      // explicit OK button can advance the flow.
      onOpenChange={() => {}}
    >
      <DialogContent showCloseButton={false} className="sm:max-w-[var(--dialog-width-confirm)]">
        <DialogHeader>
          <DialogTitle>Logged out</DialogTitle>
          <DialogDescription className="[font:var(--type-body)] text-[var(--ink-secondary)]">
            You have been logged out by another active session with the same
            user
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 flex justify-end">
          <Button type="button" onClick={onConfirm}>
            OK
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
